import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Params, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map } from 'rxjs';

import { SessionService } from '@ninety/platform/services/services/session.service';
import { DatadogLogger } from '@ninety/platform/services/vendor/datadog/datadog-logger';
import { CompanyUserListModel } from '@ninety/platform/state/app-entities/company-users/company-users-state.model';
import {
  selectCurrentCompanyUserWithActiveCompanyUsers,
  selectInactiveCompanyUsers,
} from '@ninety/platform/state/app-entities/company-users/company-users-state.selectors';
import { selectHasTier2Data } from '@ninety/platform/state/app-init/app-init.selectors';

@Injectable({
  providedIn: 'root',
})
export class SwitchCompanyGuard {
  constructor(
    private sessionService: SessionService,
    private store: Store,
    private router: Router,
    private datadogLogger: DatadogLogger
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.store.select(selectHasTier2Data).pipe(
      filter(hasTier2Data => hasTier2Data),
      concatLatestFrom(() => [
        this.store.select(selectCurrentCompanyUserWithActiveCompanyUsers),
        this.store.select(selectInactiveCompanyUsers),
      ]),
      map(([, currentAndActive, inactiveCompanyUsers]) => {
        const { currentCompanyUser, activeCompanyUsers } = currentAndActive;
        const companyId = route.url[1]?.toString();
        let nextCompanyUser: CompanyUserListModel = null;
        let currentUser: CompanyUserListModel = null;
        let inactiveCompanyUser: CompanyUserListModel = null;

        nextCompanyUser = activeCompanyUsers.find(u => u.companyId === companyId);
        inactiveCompanyUser = inactiveCompanyUsers.find(u => u.companyId === companyId);
        if (!nextCompanyUser && inactiveCompanyUser) {
          nextCompanyUser = inactiveCompanyUser;
        }

        currentUser = currentCompanyUser;

        if (!companyId || !nextCompanyUser) {
          this.datadogLogger.error({
            message: 'SwitchCompanyGuard: Access denied or company not found.',
            data: {
              attemptedCompanyId: companyId,
              currentUserCompanyId: currentUser?.companyId,
              url: state.url,
            },
          });
          return this.redirectToLogin(state);
        }

        const redirectUrl = this.getRedirectUrl(route.url, state.root.queryParams);

        if (currentUser?.companyId === companyId) {
          this.datadogLogger.info({
            message: 'SwitchCompanyGuard: Already in target company',
            data: {
              companyId,
              redirectUrl,
            },
          });
          if (redirectUrl) {
            return this.router.parseUrl(redirectUrl);
          }

          return this.redirectToHome();
        }

        try {
          this.sessionService.switchCompany(nextCompanyUser, redirectUrl);
        } catch (error) {
          this.datadogLogger.error({
            message: 'SwitchCompanyGuard: Error switching company',
            data: {
              error,
              companyId,
              redirectUrl,
            },
          });
          return this.redirectToLogin(state);
        }

        return true;
      })
    );
  }

  /**
   * Gets the redirect URL from an array of URL segments after removing the company ID from the
   * URL, preserving the route's query string (e.g. OAuth consent's client_id/redirect_uri/state/bos)
   * so a company-switch bounce doesn't drop it.
   * @returns {string} The redirect URL.
   */
  private getRedirectUrl(url: UrlSegment[], queryParams: Params): string {
    // company/:companyId/issues/:issueId
    // company/:companyId/settings/user/notifications
    // etc...
    if (url.length <= 2) return '';

    const path = `/${url.slice(2).join('/')}`;
    const queryString = new URLSearchParams(queryParams).toString();
    return queryString ? `${path}?${queryString}` : path;
  }

  private redirectToHome(): UrlTree {
    return this.router.parseUrl(`/home`);
  }

  private redirectToLogin(state: RouterStateSnapshot): UrlTree {
    const params = new URLSearchParams({ redirectUrl: state.url });
    return this.router.parseUrl(`/login?${params.toString()}`);
  }
}
